import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Car, ShoppingCart, Envelope, ChartLineUp } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/dashboard/stats`);
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = stats ? [
        { icon: Package, label: 'Produits', value: stats.products, color: 'text-blue-400', bg: 'bg-blue-500/20' },
        { icon: Car, label: 'Voitures', value: stats.cars, color: 'text-amber-400', bg: 'bg-amber-500/20' },
        { icon: ShoppingCart, label: 'Commandes', value: stats.total_orders, color: 'text-green-400', bg: 'bg-green-500/20', sub: `${stats.pending_product_orders + stats.pending_car_orders} en attente` },
        { icon: Envelope, label: 'Messages', value: stats.messages, color: 'text-purple-400', bg: 'bg-purple-500/20', sub: `${stats.new_messages} nouveaux` },
    ] : [];

    return (
        <div data-testid="admin-dashboard">
            <div className="mb-8">
                <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white">Tableau de bord</h1>
                <p className="text-mmd-silver/70 mt-1">Bienvenue dans votre espace d'administration</p>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-mmd-surface-dark border border-white/10 rounded-xl p-6 animate-pulse">
                            <div className="h-10 w-10 bg-white/10 rounded-lg mb-4" />
                            <div className="h-8 bg-white/10 rounded w-1/2 mb-2" />
                            <div className="h-4 bg-white/10 rounded w-1/3" />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((card, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="bg-mmd-surface-dark border border-white/10 rounded-xl p-6"
                            data-testid={`stat-card-${card.label.toLowerCase()}`}
                        >
                            <div className={`w-12 h-12 rounded-lg ${card.bg} flex items-center justify-center mb-4`}>
                                <card.icon size={24} className={card.color} />
                            </div>
                            <p className="text-3xl font-bold text-white mb-1">{card.value}</p>
                            <p className="text-mmd-silver/70 text-sm">{card.label}</p>
                            {card.sub && (
                                <p className={`text-xs mt-2 ${card.color}`}>{card.sub}</p>
                            )}
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Quick Actions */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-mmd-surface-dark border border-white/10 rounded-xl p-6">
                    <h2 className="font-heading text-lg font-medium text-white mb-4">Actions rapides</h2>
                    <div className="space-y-3">
                        <a href="/administration/products" className="block px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors">
                            Ajouter un produit
                        </a>
                        <a href="/administration/cars" className="block px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors">
                            Ajouter une voiture
                        </a>
                        <a href="/administration/orders" className="block px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors">
                            Gérer les commandes
                        </a>
                    </div>
                </div>

                <div className="bg-mmd-surface-dark border border-white/10 rounded-xl p-6">
                    <h2 className="font-heading text-lg font-medium text-white mb-4">Activité récente</h2>
                    <div className="text-center py-8 text-mmd-silver/50">
                        <ChartLineUp size={40} className="mx-auto mb-2" />
                        <p>Les statistiques détaillées seront disponibles prochainement</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
